import { Box, Button, Drawer, IconButton, Link } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Menu as MenuIcon, X as CloseIcon } from "lucide-react";
import { useEffect, useState, type JSX } from "react";
import { NavLink, Link as RouterLink, useLocation } from "react-router-dom";

import { useAuth } from "../../auth/AuthProvider.js";
import { PUBLIC_ROUTES } from "./content.js";

/**
 * The public site's navigation, rendered inside `TopBar`'s slot.
 *
 * Two page links and one pair of actions. The actions depend on who is
 * looking: a visitor gets Sign in + Get started (the two things a partner
 * site's header exists to offer); a signed-in reseller gets Open portal,
 * because "Sign in" would be a lie; an applicant gets Continue onboarding,
 * the one page their cookie opens.
 *
 * Below `md` the whole thing folds into a hamburger and an MUI Drawer. The
 * Drawer is used rather than a hand-rolled panel for the parts that are
 * easy to get wrong: focus trap, Escape, scroll lock, and returning focus
 * to the button on close. It closes itself on any navigation.
 *
 * The desktop/mobile split is a JS `useMediaQuery` check, deliberately NOT
 * a `sx={{ display: { xs, md } }}` pair of always-mounted branches: this
 * repo's test environment (happy-dom 15.11.7) does not evaluate `@media`
 * conditions when resolving `getComputedStyle` — it always applies whatever
 * non-`xs` breakpoint value is present, so a CSS-only hamburger would
 * compute `display: none` (and drop out of the accessibility tree) no
 * matter the actual viewport, making it permanently unreachable by
 * `getByRole` in `SiteNav.test.tsx`. `useMediaQuery` reads `window.
 * matchMedia`, which happy-dom *does* evaluate correctly against its real
 * viewport width, so the test can force the mobile branch by resizing the
 * window before rendering (see `setViewportWidth` there — a plain
 * `window.innerWidth = n` does not actually do it under Vitest). This also
 * means exactly one of the two navigations is ever mounted, which is why
 * the desktop `<nav aria-label="Site">` and the drawer's `<nav
 * aria-label="Site menu">` can never collide even though they carry
 * different labels defensively.
 *
 * The bar is fixed white (see `TopBar`), so the ink here is taken from
 * `common.black` and the accent — never `text.primary`, which flips. The
 * drawer's paper DOES follow the theme, so its own link/action colours use
 * `text.primary` instead (see `ActionButton`'s `onPaper` prop).
 */
const LINKS = [
  { label: "Home", to: PUBLIC_ROUTES.home, end: true },
  { label: "Reseller Program", to: PUBLIC_ROUTES.program, end: false },
] as const;

interface Action {
  label: string;
  to: string;
  variant: "text" | "contained";
}

function useActions(): Action[] {
  const { user, applicant } = useAuth();
  if (user) return [{ label: "Open portal", to: PUBLIC_ROUTES.home, variant: "contained" }];
  if (applicant) return [{ label: "Continue onboarding", to: "/onboarding", variant: "contained" }];
  return [
    { label: "Sign in", to: PUBLIC_ROUTES.login, variant: "text" },
    { label: "Get started", to: PUBLIC_ROUTES.register, variant: "contained" },
  ];
}

const inkLink = {
  fontSize: 14,
  fontWeight: 600,
  textDecoration: "none",
  color: (t: { palette: { common: { black: string } } }) => alpha(t.palette.common.black, 0.7),
  py: 0.5,
  borderBottom: "2px solid transparent",
  "&:hover": { color: "common.black" },
  "&[aria-current=page]": { color: "common.black", borderBottomColor: "accent.500" },
} as const;

export function SiteNav(): JSX.Element {
  const theme = useTheme();
  // `noSsr: true` — this SPA never server-renders, so there is no hydration
  // mismatch to guard against, and skipping the guard means the first
  // client render already reflects the real viewport (no flash of the
  // wrong branch, and nothing for a test to `act()` around).
  const isCompact = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  const actions = useActions();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  // Any navigation — a link in the drawer, Back, a redirect — closes it.
  useEffect(() => setOpen(false), [pathname]);

  // A breakpoint flip unmounts the Drawer branch entirely, so an open
  // drawer would come back — scroll lock and all — the moment the window
  // narrows again. Close it on the way out instead.
  useEffect(() => {
    if (!isCompact) setOpen(false);
  }, [isCompact]);

  if (isCompact) {
    return (
      <>
        <IconButton
          aria-label="Open menu"
          aria-expanded={open}
          // Only while the drawer is mounted: `aria-controls` pointing at an
          // id that is not in the document is a dangling reference, and
          // some screen readers announce it as a broken control.
          {...(open ? { "aria-controls": "site-menu" } : {})}
          onClick={() => setOpen(true)}
          sx={{ color: "common.black" }}
        >
          <MenuIcon size={22} />
        </IconButton>

        <Drawer
          anchor="right"
          open={open}
          onClose={() => setOpen(false)}
          PaperProps={{
            id: "site-menu",
            // MUI's Modal (Drawer's base) puts no role on the panel itself;
            // the nested `<nav aria-label="Site menu">` already names the
            // content, so the panel just needs the role to be announced —
            // and found — as a dialog.
            role: "dialog",
            "aria-modal": true,
            // The panel needs its own name: the nested `<nav aria-label="Site
            // menu">` names the navigation inside it, not the dialog.
            "aria-label": "Site menu",
            sx: { width: 300, maxWidth: "88vw", bgcolor: "background.paper" },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
            <IconButton aria-label="Close menu" onClick={() => setOpen(false)}>
              <CloseIcon size={22} />
            </IconButton>
          </Box>
          <Box component="nav" aria-label="Site menu" sx={{ display: "flex", flexDirection: "column", px: 3, gap: 1 }}>
            {LINKS.map((l) => (
              <Link
                key={l.to}
                component={NavLink}
                to={l.to}
                end={l.end}
                sx={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: "text.primary",
                  textDecoration: "none",
                  py: 1,
                  "&[aria-current=page]": { color: "accent.500" },
                }}
              >
                {l.label}
              </Link>
            ))}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 3 }}>
              {actions.map((a) => (
                <ActionButton key={a.to} action={a} fullWidth onPaper />
              ))}
            </Box>
          </Box>
        </Drawer>
      </>
    );
  }

  return (
    <>
      <Box component="nav" aria-label="Site" sx={{ display: "flex", alignItems: "center", gap: 3.5, mr: 4 }}>
        {LINKS.map((l) => (
          <Link key={l.to} component={NavLink} to={l.to} end={l.end} sx={inkLink}>
            {l.label}
          </Link>
        ))}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mr: 3 }}>
        {actions.map((a) => (
          <ActionButton key={a.to} action={a} />
        ))}
      </Box>
    </>
  );
}

function ActionButton({
  action,
  fullWidth = false,
  onPaper = false,
}: {
  action: Action;
  fullWidth?: boolean;
  /** True inside the Drawer, whose paper follows the theme — the text
   * variant's ink there must be `text.primary`, not `common.black`, which
   * would vanish in dark mode. */
  onPaper?: boolean;
}): JSX.Element {
  const contained = action.variant === "contained";
  return (
    <Button
      component={RouterLink}
      to={action.to}
      variant={contained ? "contained" : "text"}
      fullWidth={fullWidth}
      disableElevation
      sx={{
        borderRadius: 999,
        px: contained ? 2.5 : 1.5,
        fontWeight: 600,
        textTransform: "none",
        ...(contained
          ? {
              color: "accent.contrastText",
              backgroundImage: (t) => `linear-gradient(135deg, ${t.palette.accent[600]}, ${t.palette.accent[800]})`,
              "&:hover": { backgroundImage: (t) => `linear-gradient(135deg, ${t.palette.accent[500]}, ${t.palette.accent[700]})` },
            }
          : onPaper
            ? {
                color: "text.primary",
                "&:hover": { bgcolor: "action.hover" },
              }
            : {
                color: (t) => alpha(t.palette.common.black, 0.75),
                "&:hover": { color: "common.black", bgcolor: (t) => alpha(t.palette.common.black, 0.04) },
              }),
      }}
    >
      {action.label}
    </Button>
  );
}
